import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { catalogueApi } from "../../api/catalogue";
import { CorporateBand } from "../../components/catalogue/home/CorporateBand";
import { DepartmentTiles } from "../../components/catalogue/home/DepartmentTiles";
import { FeaturedRow } from "../../components/catalogue/home/FeaturedRow";
import { HomeBanner } from "../../components/catalogue/home/HomeBanner";
import { HomeSearchBar } from "../../components/catalogue/home/HomeSearchBar";
import { KitCards } from "../../components/catalogue/home/KitCards";
import { MobileHome } from "../../components/catalogue/home/MobileHome";
import { QuickOrder } from "../../components/catalogue/home/QuickOrder";
import { RecentlyViewed } from "../../components/catalogue/home/RecentlyViewed";
import { TrustStrip } from "../../components/catalogue/home/TrustStrip";
import { MachinePicker } from "../../components/catalogue/MachinePicker";
import { LoadError } from "../../components/catalogue/Notices";
import { searchPath } from "../../components/catalogue/SearchBox";
import { PageWidth } from "../../components/catalogue/StoreShell";
import { Card } from "../../components/ui";
import { recentlyViewed, recentSearches } from "../../lib/recent";
import { useCustomerSession } from "../account/session";
import { CardAddButton } from "./CardAddButton";
import { usePageTitle } from "./usePageTitle";
import { useQuickOrder } from "./useQuickOrder";

/**
 * PRD screen 1, the Claude Design home screen (KALI-31): search, quick order and parts to buy
 * first, with the machine picker further down. A section with nothing to show is left out; one
 * that failed to load says so.
 */
export function StorefrontRoute() {
  usePageTitle(null);
  const navigate = useNavigate();
  const shelves = useQuery({ queryKey: ["catalogue", "home"], queryFn: catalogueApi.home });
  const types = useQuery({ queryKey: ["catalogue", "product_types"], queryFn: catalogueApi.productTypes });
  // The phone home's "Find your machine" carousel. Same key as PickYourMachine, so it's one request.
  const families = useQuery({ queryKey: ["catalogue", "machines"], queryFn: catalogueApi.machineFamilies });
  // Read once: the page doesn't reshuffle while the visitor is on it.
  const [viewedIds] = useState(recentlyViewed.list);
  const [searches] = useState(recentSearches.list);
  const viewed = useQuery({ queryKey: ["catalogue", "listings", viewedIds], queryFn: () => catalogueApi.listings(viewedIds), enabled: viewedIds.length > 0 });
  const quickOrder = useQuickOrder();
  const company = useCustomerSession().data?.user?.company;

  return (
    <>
      <h1 className="sr-only">Kalikaya online parts catalogue</h1>

      {/* The phone (≤768px) home, rebuilt to the mobile kit (KALI-48). Desktop keeps its own layout below. */}
      <div className="md:hidden">
        <MobileHome
          shelves={shelves}
          departments={types}
          machines={families}
          quickOrder={<QuickOrder {...quickOrder} />}
          machinePicker={<PickYourMachine />}
        />
      </div>

      <div className="hidden md:block">
      <HomeSearchBar departments={types.data ?? []} recent={searches} onSearch={(query, typeId) => navigate(searchPath(query, typeId))} />
      <TrustStrip />

      <PageWidth className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
        {shelves.isPending ? <div aria-busy="true" className="min-h-[340px] rounded-xl bg-green-900" /> : <HomeBanner listing={shelves.data?.banner ?? null} />}
        <QuickOrder {...quickOrder} />
      </PageWidth>

      {types.isError ? (
        <Section>
          <LoadError onRetry={() => types.refetch()} />
        </Section>
      ) : (
        types.data &&
        types.data.length > 0 && (
          <Section>
            <DepartmentTiles types={types.data} />
          </Section>
        )
      )}

      {shelves.isError && (
        <Section>
          <LoadError onRetry={() => shelves.refetch()} />
        </Section>
      )}
      {shelves.data && shelves.data.featured.length > 0 && (
        <Section>
          <FeaturedRow listings={shelves.data.featured} footer={(listing) => <CardAddButton listing={listing} />} />
        </Section>
      )}
      {shelves.data && shelves.data.kits.length > 0 && (
        <Section>
          <KitCards kits={shelves.data.kits} />
        </Section>
      )}

      <Section id="pick-machine">
        <PickYourMachine />
      </Section>

      {viewed.data && viewed.data.length > 0 && (
        <Section>
          <RecentlyViewed listings={viewed.data.slice(0, 6)} />
        </Section>
      )}

      {/* A corporate account's login already has its prices. */}
      {!company && (
        <Section>
          <CorporateBand />
        </Section>
      )}
      </div>
    </>
  );
}

function Section({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <PageWidth className="mt-14 scroll-mt-24">
      <div id={id}>{children}</div>
    </PageWidth>
  );
}

/** Family, machine, assembly, then the diagram (desktop kit MachinePicker), below the shelves. */
function PickYourMachine() {
  const navigate = useNavigate();
  const families = useQuery({ queryKey: ["catalogue", "machines"], queryFn: catalogueApi.machineFamilies });
  const [chosen, setChosen] = useState<{ familyId?: number; machineId?: number; assemblyId?: number }>({});
  const list = families.data ?? [];
  const family = list.find((f) => f.id === chosen.familyId) ?? list[0];
  const machineId = chosen.machineId ?? family?.equipment_models[0]?.id;
  const machine = useQuery({ queryKey: ["catalogue", "machine", machineId], queryFn: () => catalogueApi.machine(machineId!), enabled: machineId !== undefined });
  const assemblyId = chosen.assemblyId ?? machine.data?.assemblies[0]?.id ?? null;

  return (
    <Card padding="var(--space-8)" className="grid items-center gap-8 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-10">
      <div>
        <p className="mb-3 type-eyebrow tracking-[var(--tracking-widest)] text-green-700 uppercase">Not sure which part?</p>
        <h2 className="mb-3 type-h2 [font-size:var(--text-xl)]">Pick your machine and read it off the diagram</h2>
        <p className="mb-0 type-small text-text-muted">Every assembly is drawn with numbered positions against the parts list, so you order the right part first time.</p>
      </div>
      <div>
        {families.isPending ? (
          <p className="mb-0 type-body text-text-muted">Loading machines…</p>
        ) : families.isError ? (
          <LoadError onRetry={() => families.refetch()} />
        ) : !family ? (
          <p className="mb-0 type-body text-text-muted">No diagrams are online yet. Check back soon.</p>
        ) : (
          <>
            <MachinePicker
              families={list}
              familyId={family.id}
              machineId={machineId!}
              machine={machine.data}
              assemblyId={assemblyId}
              onFamily={(id) => setChosen({ familyId: id })}
              onMachine={(id) => setChosen({ familyId: family.id, machineId: id })}
              onAssembly={(id) => setChosen({ ...chosen, assemblyId: id })}
              onOpen={() => assemblyId !== null && navigate(`/assemblies/${assemblyId}`)}
            />
            {/* Otherwise the diagram list would sit on "Loading…" for good. */}
            {machine.isError && (
              <p role="alert" className="mt-3 mb-0 type-small text-danger-700">
                Couldn't load this machine's diagrams.{" "}
                <button type="button" onClick={() => machine.refetch()} className="cursor-pointer underline">
                  Try again
                </button>
              </p>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
